import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { timeoff } from '@cwos/api-client';
import {
  ConsequenceNote,
  Pill,
  SkeletonSet,
  SuccessEmpty,
  TableCard,
  type Column,
} from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { SettingsCard, SettingsHeader } from './common.js';
import { LoadFailure } from '../LoadFailure.js';

type TType = timeoff.TimeOffType;

/**
 * Time-off types (doc 01 A6.7, doc 13 T4). Each type’s "quiets missing-hours
 * flags" toggle is stated as a consequence — approving that type intentionally
 * silences hour-gap alerts for those days.
 */
export function TimeoffSettings() {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canManage = has('timeoff:manage');

  const list = useQuery({ queryKey: ['timeoff', 'types'], queryFn: () => timeoff.types() });

  const [name, setName] = useState('');
  const [paid, setPaid] = useState(false);
  const [suppresses, setSuppresses] = useState(false);

  const create = useMutation({
    mutationFn: () => timeoff.createType({ name: name.trim(), paid, suppressesAlerts: suppresses }),
    onSuccess: () => {
      setName('');
      setPaid(false);
      setSuppresses(false);
      void qc.invalidateQueries({ queryKey: ['timeoff', 'types'] });
    },
  });

  const columns: Column<TType>[] = [
    {
      key: 'name',
      header: 'Type',
      render: (r) => <span style={{ fontWeight: 600 }}>{r.name}</span>,
    },
    { key: 'paid', header: 'Paid', render: (r) => (r.paid ? 'Paid' : 'Unpaid') },
    {
      key: 'suppress',
      header: 'Missing-hours flags',
      render: (r) =>
        r.suppressesAlerts ? (
          <Pill variant="good" dot>
            Quieted
          </Pill>
        ) : (
          <Pill variant="plain" dot>
            Not affected
          </Pill>
        ),
    },
    {
      key: 'active',
      header: 'Status',
      render: (r) => (
        <Pill variant={r.active ? 'good' : 'plain'} dot>
          {r.active ? 'Active' : 'Inactive'}
        </Pill>
      ),
    },
  ];

  return (
    <div className="rn-col" style={{ maxWidth: 720 }}>
      <SettingsHeader title="Time-off types" />

      {list.isLoading ? (
        <SkeletonSet surface="table" rows={4} />
      ) : list.isError && !list.data ? (
        <LoadFailure
          error={list.error}
          message="We couldn’t load time-off types just now."
          onRetry={() => void list.refetch()}
        />
      ) : (list.data ?? []).length === 0 ? (
        <SuccessEmpty heading="No types yet" message="Add a time-off type to get started." />
      ) : (
        <TableCard columns={columns} rows={list.data ?? []} rowKey={(r: TType) => r.id} />
      )}

      {canManage ? (
        <SettingsCard>
          <h2 className="rn-card-title">Add a type</h2>
          <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-10)' }}>
            <input
              style={{ marginBottom: 0 }}
              placeholder="Type name (e.g. Vacation)"
              aria-label="Type name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
              <input type="checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)} />
              <span>Paid time off</span>
            </label>
            <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={suppresses}
                onChange={(e) => setSuppresses(e.target.checked)}
              />
              <span>Quiets missing-hours flags on these days</span>
            </label>
            <ConsequenceNote text="Approving this type quiets missing-hours flags for those days, on purpose." />
            <div>
              <button
                type="button"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={create.isPending || name.trim() === ''}
                onClick={() => create.mutate()}
              >
                {create.isPending ? 'Saving…' : 'Add type'}
              </button>
            </div>
          </div>
        </SettingsCard>
      ) : null}
    </div>
  );
}
